// Pure geometry for the Studio crop tool. Every rect is { x, y, w, h } in the source's own
// pixels, so it survives being shown at any size and can be stored on the media item to
// reopen a crop exactly where it was left.

const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));

// Preset ratio as a single number (w / h), or null for free-form.
export const presetRatio = (preset) =>
  preset && Number(preset.ratioW) > 0 && Number(preset.ratioH) > 0 ? Number(preset.ratioW) / Number(preset.ratioH) : null;

// The largest rect of the given ratio that fits the source, centred. Free-form → the whole frame.
export function centeredRect(W, H, ratio) {
  if (!ratio) return { x: 0, y: 0, w: W, h: H };
  let w = W, h = W / ratio;
  if (h > H) { h = H; w = H * ratio; }
  return { x: (W - w) / 2, y: (H - h) / 2, w, h };
}

export function moveRect(r, dx, dy, W, H) {
  return { ...r, x: clamp(r.x + dx, 0, W - r.w), y: clamp(r.y + dy, 0, H - r.h) };
}

// Drag a corner ('nw' | 'ne' | 'sw' | 'se') by (dx, dy); the opposite corner stays put.
// With a ratio, whichever axis moved further leads and the other follows, then the pair is
// scaled back to fit inside the frame.
export function resizeRect(start, corner, dx, dy, W, H, ratio, min = 20) {
  const east = corner.includes('e'), south = corner.includes('s');
  const ax = east ? start.x : start.x + start.w;
  const ay = south ? start.y : start.y + start.h;
  const maxW = east ? W - ax : ax;
  const maxH = south ? H - ay : ay;
  let w = Math.max(min, start.w + (east ? dx : -dx));
  let h = Math.max(min, start.h + (south ? dy : -dy));
  if (ratio) {
    if (w / h > ratio) h = w / ratio; else w = h * ratio;
    if (h > maxH) { h = maxH; w = h * ratio; }
    if (w > maxW) { w = maxW; h = w / ratio; }
  } else {
    w = Math.min(w, maxW); h = Math.min(h, maxH);
  }
  return { x: east ? ax : ax - w, y: south ? ay : ay - h, w, h };
}

// Whole pixels; video encoders (yuv420p) also need even dimensions and offsets.
export function roundRect(r, even = false) {
  const f = even ? n => Math.max(2, Math.floor(n / 2) * 2) : n => Math.max(1, Math.round(n));
  return { x: even ? Math.floor(r.x / 2) * 2 : Math.round(r.x), y: even ? Math.floor(r.y / 2) * 2 : Math.round(r.y), w: f(r.w), h: f(r.h) };
}

// Output pixel size: a preset with a width fixes it (height follows the ratio); otherwise
// the crop's own size, so nothing gets resampled.
export function outputSize(rect, preset, even = false) {
  const ratio = presetRatio(preset);
  const width = Number(preset?.width);
  const raw = ratio && width > 0 ? { w: width, h: width / ratio } : { w: rect.w, h: rect.h };
  const f = even ? n => Math.max(2, Math.round(n / 2) * 2) : n => Math.max(1, Math.round(n));
  return { w: f(raw.w), h: f(raw.h) };
}

// Already the preset's shape? Ratio within `tol` (1%) — an image that's merely bigger or
// smaller than the preset's width counts as fitting: re-cropping it would only resample.
export function fitsRatio(size, ratio, tol = 0.01) {
  if (!ratio || !size?.W || !size?.H) return false;
  return Math.abs(size.W / size.H - ratio) / ratio <= tol;
}

// Same stem, new extension — keeps "Sort by filename" order while the extension tracks the
// format actually produced (a .mov always comes back as .mp4).
export function withExt(filename, ext) {
  const stem = (filename || 'media').replace(/\.[a-z0-9]{1,5}$/i, '');
  return `${stem}.${ext}`;
}

export const formatTime = (s) => {
  if (!isFinite(s)) return '0:00.0';
  const m = Math.floor(s / 60);
  return `${m}:${(s - m * 60).toFixed(1).padStart(4, '0')}`;
};

// The media item a crop produces: the new file, plus the untouched original (kept from the
// first crop on) and the crop settings, so it can be re-cropped from full quality or reverted.
export function croppedItem(cur, uploaded, crop) {
  const { original, crop: _old, ...src } = cur;
  return { ...uploaded, original: original || src, crop };
}
